"use client";

import { useEffect, useState } from "react";

interface MemoryEntry {
  id: string;
  date: string;
  tag: string;
  title: string;
  body: string;
  decision?: {
    decision: string;
    domain: string;
    reason: string;
    evidence: string;
    alternatives?: string;
    confidence: "low" | "medium" | "high";
    reviewTrigger?: string;
    linkedSystem?: string;
    supersedes?: string;
  };
}

interface DayGroup {
  date: string;
  entries: MemoryEntry[];
}

function formatDate(dateStr: string) {
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

const TAG_COLORS: Record<string, string> = {
  "Setup":         "bg-[#e8f0e8] text-[#4a7a4a]",
  "Identity":      "bg-[#eee8f5] text-[#6a4a8a]",
  "About Weston":  "bg-[#fdf0e6] text-[#b5622a]",
  "Mission Control":"bg-[#e6eef8] text-[#3a5a8a]",
  "Google Sheets": "bg-[#e8f5ee] text-[#2a7a5a]",
  "Larsson Line":  "bg-[#fff3e0] text-[#a06010]",
  "Travel":        "bg-[#fce8ec] text-[#a03050]",
  "Decision":      "bg-[#eef2f8] text-[#385a7a]",
};

function tagClass(tag: string) {
  return TAG_COLORS[tag] ?? "bg-[#f2ede4] text-[#8a7a68]";
}

function EntryRow({ entry }: { entry: MemoryEntry }) {
  const [open, setOpen] = useState(false);
  const decisionRows = entry.decision ? [
    ["Decision", entry.decision.decision],
    ["Domain", entry.decision.domain],
    ["Reason", entry.decision.reason],
    ["Evidence", entry.decision.evidence],
    ["Alternatives", entry.decision.alternatives],
    ["Confidence", entry.decision.confidence],
    ["Review Trigger", entry.decision.reviewTrigger],
    ["Linked System", entry.decision.linkedSystem],
    ["Supersedes", entry.decision.supersedes],
  ].filter(([, value]) => Boolean(value)) : [];

  return (
    <div className="border-b border-[#ece7dc] last:border-b-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start gap-3 py-3 px-0 text-left group hover:bg-[#faf7f2] transition-colors rounded-sm"
      >
        {/* Chevron */}
        <span className={`mt-[3px] flex-shrink-0 text-[#c4b49a] transition-transform duration-150 ${open ? "rotate-90" : ""}`}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M4 2l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>

        {/* Tag */}
        <span className={`flex-shrink-0 text-[10px] px-2 py-0.5 rounded-full font-medium tracking-wide mt-[1px] ${tagClass(entry.tag)}`}>
          {entry.tag}
        </span>

        {/* Title */}
        <span className="text-sm text-[#2c2416] leading-snug font-medium group-hover:text-[#b5622a] transition-colors">
          {entry.title}
        </span>
      </button>

      {/* Expandable body */}
      {open && (
        <div className="pb-4 pl-10 pr-2 space-y-3">
          <p className="text-sm text-[#6b5c48] leading-relaxed">
            {entry.body}
          </p>
          {decisionRows.length > 0 && (
            <dl className="border-l-2 border-[#c9d4df] pl-4 py-1 space-y-2">
              {decisionRows.map(([label, value]) => (
                <div key={label} className="grid grid-cols-[110px_1fr] gap-3">
                  <dt className="text-[11px] uppercase tracking-[0.08em] text-[#8a98a8] font-semibold">
                    {label}
                  </dt>
                  <dd className="text-[13px] text-[#4f5f6f] leading-relaxed">
                    {value}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      )}
    </div>
  );
}

function DaySection({ group }: { group: DayGroup }) {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <div className="mb-8">
      {/* Date header */}
      <button
        onClick={() => setCollapsed((v) => !v)}
        className="flex items-center gap-2 mb-3 group"
      >
        <span className={`text-[#c4b49a] transition-transform duration-150 ${collapsed ? "-rotate-90" : ""}`}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
        <span className="text-[11px] tracking-[0.12em] uppercase text-[#b5622a] font-semibold group-hover:text-[#8a3a10] transition-colors">
          {formatDate(group.date)}
        </span>
        <span className="text-[11px] text-[#c4b49a]">
          · {group.entries.length} {group.entries.length === 1 ? "entry" : "entries"}
        </span>
      </button>

      {/* Entries */}
      {!collapsed && (
        <div className="ml-4 border-l-2 border-[#ece7dc] pl-4">
          {group.entries.map((entry) => (
            <EntryRow key={entry.id} entry={entry} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function MemoryPage() {
  const [groups, setGroups] = useState<DayGroup[]>([]);

  useEffect(() => {
    fetch("/api/memory")
      .then((r) => r.json())
      .then((entries: MemoryEntry[]) => {
        // Group by date, most recent first
        const map = new Map<string, MemoryEntry[]>();
        for (const e of entries) {
          if (!map.has(e.date)) map.set(e.date, []);
          map.get(e.date)!.push(e);
        }
        const sorted = Array.from(map.entries())
          .sort((a, b) => b[0].localeCompare(a[0]))
          .map(([date, entries]) => ({ date, entries }));
        setGroups(sorted);
      });
  }, []);

  return (
    <div className="max-w-3xl mx-auto px-12 py-10">
      {/* Header */}
      <div className="flex items-baseline gap-4 pb-6 mb-8 border-b border-[#e8e2d8]">
        <h1 className="text-[28px] font-serif font-normal text-[#2c2416] tracking-tight">
          Memory
        </h1>
        <span className="text-sm text-[#a0906e] italic">
          {new Date().toLocaleDateString("en-US", {
            weekday: "long", month: "long", day: "numeric", year: "numeric",
            timeZone: "America/Chicago",
          })}
        </span>
      </div>

      {/* Day groups */}
      {groups.length === 0 ? (
        <div className="py-16 text-center text-[#b0a090] italic text-sm">
          No memories yet. Robby is just getting started.
        </div>
      ) : (
        groups.map((group) => (
          <DaySection key={group.date} group={group} />
        ))
      )}
    </div>
  );
}
